

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { opsApi, fmtINR, fmtNum, fmtDate, GRAD, FONT_HEAD, STAGES, STAGE_META } from './opsApi';
import {
  Building2, GraduationCap, Users, Receipt, Wallet, BadgeCheck, Cpu,
  Trophy, ArrowUpRight, Target, LifeBuoy, MessageSquare,
} from 'lucide-react';
import { friendlyError } from '../../lib/errors';
import Loader from '../../components/Loader';

const KPI = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
    <div className="h-1" style={{ backgroundColor: GRAD }} />
    <div className="p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E8F5E9' }}>
          <Icon className="w-4 h-4 text-[#34A853]" />
        </div>
      </div>
      <div>
        <div className="text-2xl font-semibold tracking-tight tabular-nums" style={FONT_HEAD}>{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  </div>
);

export default function OpsDashboard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    opsApi.get('/dashboard').then((res) => setData(res.data)).catch((err) => {
      console.error('Dashboard load failed:', err);
      setError(friendlyError(err, 'Failed to load dashboard'));
    });
    opsApi.get('/support-tickets/stats').then((res) => setTicketStats(res.data)).catch((err) => {
      console.error('Ticket stats failed:', err);
    });
  }, []);

  if (error) {
    return <div className="flex justify-center py-24 text-red-500">Error: {error}</div>;
  }

  if (!data) {
    return <Loader />;
  }

  const isManager = data.leaderboard.length > 0 || ['super_admin', 'zone_admin', 'state_manager'].includes(data.role);
  const maxStage = Math.max(...STAGES.map((s) => data.institutions.pipeline[s] || 0), 1);

  return (
    <div className="space-y-8" data-testid="ops-dashboard">
      <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ backgroundColor: GRAD }}>
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full opacity-15 bg-white" />
        <div className="absolute -right-8 bottom-0 w-32 h-32 rounded-full opacity-10 bg-white" />
        <h1 className="relative text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>
          {data.role === 'sales_rep' ? `Welcome back, ${user.full_name.split(' ')[0]}` : 'National operations command center'}
        </h1>
        <p className="relative text-sm text-white/85 mt-1">
          Scope <span className="font-mono">{data.scope_path}</span> · {fmtNum(data.institutions.total)} institutions · {fmtNum(data.licensing.students_licensed)} licensed students
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-institutions" icon={Building2} label="Institutions"
          value={fmtNum(data.institutions.total)} sub={`${fmtNum(data.institutions.active)} active`} />
        <KPI testId="kpi-teachers" icon={GraduationCap} label="Teacher licences" value={fmtNum(data.licensing.teachers_active)} sub="active, billable" />
        <KPI testId="kpi-students" icon={Users} label="Licensed students" value={fmtNum(data.licensing.students_licensed)}
          sub={data.licensing.penetration_pct != null ? `${data.licensing.penetration_pct}% penetration` : 'institution-billed, free for students'} />
        <KPI testId="kpi-invoiced" icon={Receipt} label="Total invoiced" value={fmtINR(data.billing.total_invoiced)}
          sub={`${fmtINR(data.billing.collected)} collected`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-outstanding" icon={Receipt} label="Outstanding" value={fmtINR(data.billing.outstanding)}
          sub={data.billing.refunded ? `${fmtINR(data.billing.refunded)} refunded` : undefined} />
        <KPI testId="kpi-commission" icon={Wallet} label="Commissions pending" value={fmtINR(data.commissions.pending)}
          sub={`${fmtINR(data.commissions.paid)} paid out`} />
        <KPI testId="kpi-fleet" icon={Cpu} label="Hardware kits" value={fmtNum(data.fleet.total)}
          sub={`${fmtNum(data.fleet.deployed)} deployed · ${fmtNum(data.fleet.faulty)} faulty`} />
        <KPI testId="kpi-approvals" icon={BadgeCheck} label="Pending approvals" value={fmtNum(data.pending_approvals)}
          sub={`${fmtNum(data.open_tickets)} open resolutions`} />
      </div>

      {/* Support Tickets Card */}
      {ticketStats && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="support-tickets-overview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold flex items-center gap-2" style={FONT_HEAD}>
              <MessageSquare className="w-4 h-4 text-[#34A853]" /> Support Tickets
            </h2>
            <button onClick={() => navigate('/sales/support-tickets')} className="text-xs text-[#3B82F6] font-medium flex items-center gap-1 hover:underline">
              View all <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total', value: ticketStats.total, color: '#64748B' },
              { label: 'Open', value: ticketStats.open, color: '#3B82F6' },
              { label: 'In Progress', value: ticketStats.in_progress, color: '#F59E0B' },
              { label: 'Resolved', value: ticketStats.resolved, color: '#10B981' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="text-xl font-bold tabular-nums" style={{ color }}>{fmtNum(value || 0)}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-medium uppercase tracking-wider">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold" style={FONT_HEAD}>Institution lifecycle funnel</h2>
            <Link to="/sales/institutions" className="text-xs text-[#3B82F6] font-medium flex items-center gap-1 hover:underline">
              View institutions <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3" data-testid="lifecycle-funnel">
            {STAGES.map((s) => (
              <div key={s} className="flex items-center gap-3">
                <span className="w-24 text-xs font-medium text-slate-500">{STAGE_META[s].label}</span>
                <div className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden">
                  <div className="h-full rounded-lg"
                    style={{
                      width: `${Math.max(((data.institutions.pipeline[s] || 0) / maxStage) * 100, data.institutions.pipeline[s] ? 8 : 0)}%`,
                      background: s === 'active' ? '#34A853' : s === 'churned' ? '#ef4444' : GRAD,
                    }} />
                </div>
                <span className="w-10 text-right text-sm font-semibold tabular-nums">{data.institutions.pipeline[s] || 0}</span>
              </div>
            ))}
          </div>
          {data.revenue_trend.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Revenue trend</div>
              <div className="flex items-end gap-3 h-24">
                {data.revenue_trend.map((m) => {
                  const max = Math.max(...data.revenue_trend.map((x) => x.invoiced), 1);
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex items-end gap-1 h-16">
                        <div className="flex-1 rounded-t" style={{ height: `${(m.invoiced / max) * 100}%`, background: '#3B82F6' }} title={`Invoiced ${fmtINR(m.invoiced)}`} />
                        <div className="flex-1 bg-[#34A853] rounded-t" style={{ height: `${(m.collected / max) * 100}%` }} title={`Collected ${fmtINR(m.collected)}`} />
                      </div>
                      <span className="text-[10px] text-slate-400">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {isManager && data.leaderboard.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-base font-semibold mb-4 flex items-center gap-2" style={FONT_HEAD}>
                <Trophy className="w-4 h-4 text-amber-500" /> Rep leaderboard
              </h2>
              <div className="space-y-3" data-testid="rep-leaderboard">
                {data.leaderboard.map((l, i) => (
                  <div key={l.rep_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{l.name}</div>
                      <div className="text-xs text-slate-400">{l.activations} activations</div>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">{fmtINR(l.total_commission)}</span>
                  </div>
                ))}
              </div>
              <Link to="/sales/pipeline" className="text-xs text-[#3B82F6] font-medium flex items-center gap-1 hover:underline mt-4">
                <Target className="w-3 h-3" /> Targets & full leaderboard
              </Link>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-base font-semibold mb-4 flex items-center gap-2" style={FONT_HEAD}>
              <LifeBuoy className="w-4 h-4 text-[#34A853]" /> Recent activity
            </h2>
            <div className="space-y-4" data-testid="recent-activity">
              {data.recent_activity.length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
              {data.recent_activity.slice(0, 8).map((a) => (
                <div key={a.id} className="flex gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: '#3B82F6' }} />
                  <div className="min-w-0">
                    <div className="text-xs text-slate-700">
                      <span className="font-medium">{a.actor_name}</span>{' '}
                      <span className="text-slate-400">{(a.action || '').replace(/[._]/g, ' ')}</span>
                    </div>
                    {a.detail && <div className="text-xs text-slate-500 truncate">{a.detail}</div>}
                    <div className="text-[10px] text-slate-300">{fmtDate(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
